import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Clock3, Loader2, Mail, MapPin } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const [searchParams] = useSearchParams();
    const { t, lang } = useLang();
    const c = t.contact;
    const [form, setForm] = useState({ name: "", email: "", organization: "", service: "health-system", message: "" });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        document.title = lang === "sw" ? "Wasiliana Nasi — Hallomedics" : "Contact — Hallomedics";
    }, [lang]);

    useEffect(() => {
        const svc = searchParams.get("service");
        if (svc && t.serviceLabels[svc]) {
            setForm((f) => ({ ...f, service: svc }));
        }
    }, [searchParams, t]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, {
                name: form.name,
                email: form.email,
                organization: form.organization || null,
                service: form.service,
                message: form.message,
            });
            toast.success(c.successToast);
            setForm({ name: "", email: "", organization: "", service: "health-system", message: "" });
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" ") : detail;
            toast.error(typeof msg === "string" && msg ? msg : c.errorToast);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="pt-28 lg:pt-36" data-testid="contact-page">
            <header className="mx-auto max-w-7xl px-5 sm:px-8">
                <p className="eyebrow flex items-center gap-2.5" data-testid="contact-eyebrow">
                    <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                    {c.eyebrow}
                </p>
                <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="contact-heading" key={lang}>
                    <MaskedLine delay={0.1}>{c.h1a}</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="text-primary dark:text-teal">{c.h1b}</span>
                    </MaskedLine>
                </h1>
            </header>

            <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:py-20">
                <Reveal className="lg:col-span-7">
                    <form onSubmit={submit} className="rounded-lg border border-border bg-card p-6 sm:p-10" data-testid="contact-form">
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label htmlFor="contact-name">{c.name}</Label>
                                <Input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={c.namePh} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-email">{c.email}</Label>
                                <Input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={c.emailPh} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="contact-org">{c.org}</Label>
                                <Input id="contact-org" data-testid="contact-org-input" value={form.organization} onChange={set("organization")} placeholder={c.orgPh} />
                            </div>
                            <div className="space-y-2">
                                <Label>{c.service}</Label>
                                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                                    <SelectTrigger data-testid="contact-service-select" aria-label="Service type">
                                        <SelectValue placeholder={c.servicePh} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(t.serviceLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key} data-testid={`service-option-${key}`}>
                                                {label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 sm:col-span-2">
                                <Label htmlFor="contact-message">{c.message}</Label>
                                <Textarea
                                    id="contact-message"
                                    data-testid="contact-message-input"
                                    required
                                    minLength={10}
                                    rows={6}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder={c.messagePh}
                                />
                            </div>
                        </div>
                        <Button type="submit" disabled={sending} data-testid="contact-submit-button" className="mt-8 h-12 rounded-full px-8 text-sm font-semibold">
                            {sending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {c.sending}
                                </>
                            ) : (
                                <>
                                    {c.submit}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </>
                            )}
                        </Button>
                    </form>
                </Reveal>

                <div className="lg:col-span-5">
                    <Reveal delay={0.12}>
                        <div className="rounded-lg bg-primary p-7 text-primary-foreground sm:p-9" data-testid="contact-next-steps">
                            <p className="font-mono text-xs uppercase tracking-[0.22em] text-teal">{c.nextEyebrow}</p>
                            <ol className="mt-6 space-y-6">
                                {c.steps.map((s, i) => (
                                    <li key={s.title} className="flex gap-4">
                                        <span className="font-heading text-2xl font-extrabold text-teal">0{i + 1}</span>
                                        <div>
                                            <p className="font-heading text-base font-semibold">{s.title}</p>
                                            <p className="mt-1 text-sm leading-relaxed text-primary-foreground/70">{s.copy}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-6 space-y-5 rounded-lg border border-border bg-card p-7 sm:p-9" data-testid="contact-details">
                            <p className="flex items-start gap-3 text-sm text-muted-foreground">
                                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                                <a href="mailto:hello@hallomedics.com" data-testid="contact-email-link" className="font-medium text-foreground transition-colors hover:text-primary dark:hover:text-teal">
                                    hello@hallomedics.com
                                </a>
                            </p>
                            <p className="flex items-start gap-3 text-sm text-muted-foreground">
                                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                                {c.office}
                            </p>
                            <p className="flex items-start gap-3 text-sm text-muted-foreground">
                                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" />
                                {c.hours}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Contact;
